import React, { useState, useEffect, useMemo } from 'react';
import { Button, Dialog } from '@betareader/design-system';
import { toast } from 'react-toastify';
import {useDispatch, useSelector} from '../../reduxHooks';
import { Trans, useTranslation } from 'react-i18next';

import InvitationForm from './invitationForm';
import BulkInvitationForm from './bulkInvitationForm';
import ReadingNotifications from './reading-notifications';
import JoinBetaFormDesigner from './signup-form/joinBetaFormDesigner';
import ProductLimitMessage from '../../components/standard-messages/productLimitMessage';
import PublicUserProfile from '../user/publicUserProfile';
import BroadcastMessage from './bulk-actions/broadcastMessage';
import ReaderEntryTableRow from './readerEntryTableRow';
import BookSpecificRating from './bookSpecificRating';
import { PaddedWrapper } from '../../style';
import { useParams, useHistory } from 'react-router';

// UX tracking
import {
  authUtil,
  uxAnalyticsUtil,
  copyToClipBoardUtil,
  productCapUtil,
  accessUtil,
  featureToggleUtil,
} from '../../utils';

import {
  updateReader,
  deleteReader,
  fetchReaders,
  fetchJoinRequests,
  sortReaderList,
  toggleReaderSelection,
  sendInvitation,
  approveJoinRequest,
  declineJoinRequest,
  sendSurveyReminder,
} from '../../modules/reader';
import download from 'downloadjs';
// const download = require('downloadjs');

const ReaderList = () => {
  // globals
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();

  // redux state
  const readers = useSelector(state => state.reader.readers) ?? [];
  const currentBook = useSelector(state => state.book.currentBook);
  const joinRequests = useSelector(state => state.reader.joinRequests);
  const userProfile = useSelector(state => state.user.userProfile);
  const activeAccount = useSelector(state => state.account.activeAccount);
  const sorting = useSelector(state => state.reader.sorting);

  // local state
  const [isLoading, setIsLoading] = useState(false);
  const [menuActiveIndex, setMenuActiveIndex] = useState(0);
  const [localReaders, setLocalReaders] = useState<any[]>([]);
  const [showInvitationForm, setShowInvitationForm] = useState<
    'single' | 'bulk' | false
  >(false);
  const [joinRequestToBeConfirmed, setJoinRequestToBeConfirmed] =
    useState<{ joinRequest: any; contentVersion: any; swap: any } | null>(null);
  const [showReaderProfile, setShowReaderProfile] = useState<
    string | { userId: string; autoOpenRating: boolean } | false
  >(false);
  const [showBookRatingModal, setShowBookRatingModal] = useState<any>(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // functions
  useEffect(() => {
    const setup = async () => {
      uxAnalyticsUtil.trackPageView(history.location.pathname);
      const idToken = await authUtil.getFreshIdToken();
      dispatch(fetchReaders(idToken, bookId, []));
      dispatch(fetchJoinRequests(idToken, bookId));
    };
    setup();
  }, [bookId, dispatch]);

  const invitationSent = (data: any) => {
    setLocalReaders(prev => {
      const newReaders = [...prev];
      newReaders.push(data);
      return newReaders;
    });
  };

  const invitationRemoved = (id: any) => {
    setLocalReaders(prev => {
      const newReaders = [...prev];
      const index = newReaders.findIndex(reader => reader.id === id);
      if (index > -1) {
        newReaders.splice(index, 1);
      }
      return newReaders;
    });
  };

  const handleSort = (event: any) => {
    const { sortColumn, sortDirection } = event.currentTarget.dataset;
    dispatch(
      sortReaderList(
        sortColumn,
        sortDirection === 'descending' ? 'ascending' : 'descending'
      )
    );
  };

  const handleDeleteReaderClick = async (readerId: string) => {
    const idToken = await authUtil.getFreshIdToken();
    const reader = readers.find((r: any) => r._id === readerId);
    if (
      reader &&
      ['SENT', 'CREATED', 'APPROVED', 'DECLINED'].includes(
        reader.invitation.status
      )
    ) {
      dispatch(deleteReader(idToken, reader));
    }
  };

  const handleMessageUserClick = (readerId: string) => {
    const reader = readers.find((entry: any) => entry._id === readerId);
    if (reader && reader.user) {
      history.push(`/inbox/conversations/${reader.user._id}`);
    }
  };

  const handleGoToProfileClick = (readerId: string) => {
    const reader = readers.find((entry: any) => entry._id === readerId);
    if (reader && reader.user) {
      history.push(`/u/${reader.user._id}`);
    }
  };

  const removeInvitation = async (id: any) => {
    const idToken = await authUtil.getFreshIdToken();

    fetch(
      `${process.env.REACT_APP_API_HOST}/api/books/${currentBook._id}/invitation/${id}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'br-token': idToken,
        },
      }
    )
      .then(res => res.json())
      .then(() => {
        invitationRemoved(id);
      })
      .catch(err => {
        toast.error('SomethingWentWrong');
        console.log('err:', err);
      });
  };

  const handleToggleReaderEnabledClick = async (readerId: string) => {
    const idToken = await authUtil.getFreshIdToken();
    const reader = readers.find((reader: any) => {
      return reader._id === readerId;
    });
    if (reader) {
      // reader.enabled = !reader.enabled;
      const updateData = { enabled: !reader.enabled };
      dispatch(updateReader(idToken, currentBook._id, reader._id, updateData));
    }
  };

  const handleToggleFavorite = async (readerId: string, favorite: boolean) => {
    const idToken = await authUtil.getFreshIdToken();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${currentBook._id}/readers/${readerId}/favorite`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken,
          },
          body: JSON.stringify({ favorite }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update favorite reader');
      }
      dispatch(fetchReaders(idToken, bookId, []));
    } catch (error) {
      toast.error(t('SomethingWentWrong'));
    }
  };

  const doSendInvitation = async (eventOrReaderId: any) => {
    let readerId: string | undefined;

    // Handle both event object (from direct click) and readerId string (from component props)
    if (typeof eventOrReaderId === 'string') {
      readerId = eventOrReaderId;
    } else {
      // It's an event object
      eventOrReaderId.stopPropagation();
      eventOrReaderId.preventDefault();
      readerId = eventOrReaderId.currentTarget.dataset.readerId;
    }

    const idToken = await authUtil.getFreshIdToken();
    const readerEntry = readers.find((reader: any) => reader._id === readerId);
    if (readerEntry) {
      dispatch(sendInvitation(idToken, readerEntry));
    }
  };

  const getOpportunityUrl = (bookId: any, invitationToken: any) => {
    const gaTracking =
      'utm_source=offline-communication&utm_medium=get-invitation-link&utm_campaign=get-invitation-link';
    const actionUrl = `${process.env.REACT_APP_WEB_HOST}/discover/${bookId}?token=${invitationToken}&${gaTracking}`;
    toast.info(t('CopiedXToClipboard', { x: actionUrl }));
    return actionUrl;
  };

  const copyURLToClipBoard = (token: string) => {
    const url = getOpportunityUrl(currentBook._id, token);
    copyToClipBoardUtil.copyItemToClipBoard(url);
  };

  const downloadReaderList = async () => {
    const idToken = await authUtil.getFreshIdToken();
    fetch(
      `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${currentBook._id}/readers?format=csv`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'br-token': idToken,
        },
      }
    )
      .then(res => res.blob())
      .then(file => {
        download(file, 'readers.csv', 'text/csv');
        uxAnalyticsUtil.trackEvent({
          category: 'Book Management',
          action: 'downloaded-reader-list',
        });
      })
      .catch(err => {
        console.log('err:', err);
      });
  };

  const doApproveJoinRequest = (joinRequest: any, contentVersion: any) => {
    if (joinRequest) {
      setJoinRequestToBeConfirmed({
        joinRequest,
        contentVersion,
        swap: joinRequest.swap,
      });
    }
  };

  const confirmApproval = async () => {
    if (joinRequestToBeConfirmed) {
      console.log('confirmApproval:', joinRequestToBeConfirmed);
      // reset state and approve request
      const idToken = await authUtil.getFreshIdToken();
      const { joinRequest, contentVersion } = joinRequestToBeConfirmed;
      dispatch(approveJoinRequest(idToken, joinRequest, { contentVersion }));
      setJoinRequestToBeConfirmed(null);
    }
  };

  useEffect(() => {
    // if we have a join request to be confirmed with no swap, confirm immediately
    if (!!joinRequestToBeConfirmed && !joinRequestToBeConfirmed.swap) {
      confirmApproval();
    }
  }, [joinRequestToBeConfirmed]);

  const cancelApproval = () => {
    setJoinRequestToBeConfirmed(null);
  };

  const doDeclineJoinRequest = async (readerId: string) => {
    const idToken = await authUtil.getFreshIdToken();
    const joinRequest = joinRequests.find(
      (joinRequest: any) => joinRequest._id === readerId
    );
    if (joinRequest) {
      dispatch(declineJoinRequest(idToken, joinRequest));
    }
  };

  const doSendSurveyReminder = async (joinRequest: any) => {
    const idToken = await authUtil.getFreshIdToken();
    dispatch(sendSurveyReminder(idToken, joinRequest));
  };

  const doShowReaderProfile = (userId?: string) => {
    if (userId) {
      setShowReaderProfile(userId);
    }
  };

  const doRateReader = (userId: string) => {
    // Find the reader object for the book-specific rating modal
    const reader = readers.find((r: any) => r.user?._id === userId);
    if (reader) {
      setShowBookRatingModal(reader);
    }
  };

  const doCloseReaderProfile = () => {
    setShowReaderProfile(false);
  };

  const doCloseBookRatingModal = () => {
    setShowBookRatingModal(null);
  };

  const onRatingUpdated = async () => {
    // Refresh the reader list to show updated rating
    const idToken = await authUtil.getFreshIdToken();
    dispatch(fetchReaders(idToken, bookId, []));
  };

  const onReaderSelectionChange = (readerId: string) => {
    dispatch(toggleReaderSelection(readerId));
  };

  const renderReaderTable = (readerInvitationStatus: string[]) => {
    const hasSelectedReaders = readers.some((reader: any) => reader.selected);
    const canAddMoreReaders = productCapUtil.canAddMoreReaders({
      activeAccount, userProfile, readers, currentBook,
    });
    const filteredReaderList = readers.filter((reader: any) =>
      readerInvitationStatus.includes(reader.invitation.status) &&
      (!showFavoritesOnly || reader.isFavorite)
    );
    const contentVersionOptions = currentBook.content.map((version: any, index: number) => ({
      key: version._id,
      value: version._id,
      text: `v${Number.isInteger(version.versionNumber) ? version.versionNumber : index + 1}` +
        (index === currentBook.content.length - 1 ? ` (${t('latestVersion')})` : ''),
    }));
    const canBatchEdit = featureToggleUtil.hasFeatureAccess(userProfile, 'broadcast');
    const showReading = readerInvitationStatus.includes('ACCEPTED');
    const sortHeader = (column: string, label: string) => (
      <th scope='col' key={column}>
        <button type='button' data-sort-column={column}
          data-sort-direction={sorting?.column === column ? sorting.direction : undefined}
          onClick={handleSort} aria-label={`${t('SortBy')} ${label}`}>
          {label} {sorting?.column === column ? (sorting.direction === 'ascending' ? '↑' : '↓') : ''}
        </button>
      </th>
    );

    return (
      <>
        <Dialog open={Boolean(joinRequestToBeConfirmed?.swap)}
          title={t('Confirm')} onClose={cancelApproval}
          actions={<>
            <Button variant='secondary' onClick={cancelApproval}>{t('Cancel')}</Button>
            <Button onClick={confirmApproval}>{t('Accept')}</Button>
          </>}>
          {joinRequestToBeConfirmed?.swap && (
            <>
              <Trans i18nKey='ThisIsASwapRequestForX'
                values={{x: joinRequestToBeConfirmed.swap.title}}>
                <p>This is a swap request for{' '}
                  <a href={`/discover/${joinRequestToBeConfirmed.swap._id}`}
                    target='betareader.io_reader'>{joinRequestToBeConfirmed.swap.title}</a>.
                  If you approve this request, you will be added as a reader for that book.
                </p>
              </Trans>
              <em>{t('ThisIsABetaFeature')}</em>
            </>
          )}
        </Dialog>
        {hasSelectedReaders && canBatchEdit && <BroadcastMessage />}
        <div style={{display: 'flex', justifyContent: 'flex-end'}}>
          <Button variant={showFavoritesOnly ? 'secondary' : 'ghost'} size='small'
            aria-pressed={showFavoritesOnly}
            onClick={() => setShowFavoritesOnly(value => !value)}>
            {t('FavoriteReadersOnly')}
          </Button>
        </div>
        <div style={{overflowX: 'auto'}}>
          <table className='br-table'>
            <thead><tr>
              {canBatchEdit && <th scope='col'>{t('Select')}</th>}
              <th scope='col'>{t('Profile')}</th>
              {sortHeader('name', t('Readers'))}
              <th scope='col'>{t('SocialProfiles')}</th>
              {sortHeader('createdAt', t('Created'))}
              {sortHeader('contentVersionNumber', t('Version'))}
              {showReading && sortHeader('firstSeenAt', t('StartedReading'))}
              {showReading && sortHeader('lastSeenAt', t('LastSeenAt'))}
              {showReading && sortHeader('partIndex', t('CurrentPosition'))}
              <th scope='col'>{t('Actions')}</th>
              {sortHeader('isFavorite', t('SortByFavorite'))}
            </tr></thead>
            <tbody>
              {filteredReaderList.map((reader: any) => (
                <ReaderEntryTableRow
                  key={reader._id}
                  reader={reader}
                  isEditable={accessUtil.isAllowed(currentBook.access, ['edit'])}
                  contentVersionOptions={contentVersionOptions}
                  canAddMoreReaders={canAddMoreReaders}
                  isSelectable={canBatchEdit}
                  onDelete={handleDeleteReaderClick}
                  onMessage={handleMessageUserClick}
                  onGoToProfile={handleGoToProfileClick}
                  onToggleEnabled={handleToggleReaderEnabledClick}
                  onShowReaderProfile={doShowReaderProfile}
                  onSelectedChange={onReaderSelectionChange}
                  onCopyURLToClipBoard={copyURLToClipBoard}
                  onApproveJoinRequest={doApproveJoinRequest}
                  onDeclineJoinRequest={doDeclineJoinRequest}
                  onSendInvitation={doSendInvitation}
                  onRateReader={doRateReader}
                  onToggleFavorite={handleToggleFavorite}
                  onUpdateReader={async (book, readerId, data) => {
                    const idToken = await authUtil.getFreshIdToken();
                    dispatch(updateReader(idToken, book, readerId, data));
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const hasReaders = readers && readers.length > 0;
  const totalActiveReaders = useMemo(
    () =>
      readers.filter(
        (reader: any) =>
          ['APPROVED', 'ACCEPTED', 'ABANDONED'].includes(
            reader.invitation.status
          ) && reader.enabled
      ).length,
    [readers]
  );

  const totalPendingInvitations = useMemo(
    () =>
      readers.filter((reader: any) =>
        ['SENT', 'CREATED'].includes(reader.invitation.status)
      ).length,
    [readers]
  );

  const totalPendingRequests = useMemo(
    () =>
      readers.filter((reader: any) => reader.invitation.status === 'REQUESTED').length,
    [readers]
  );

  const canAddMoreReaders = productCapUtil.canAddMoreReaders({
    activeAccount,
    userProfile,
    readers,
    currentBook,
  });

  const showProductLimitMessage =
    !canAddMoreReaders &&
    currentBook.privacy !== 'unpublished' &&
    currentBook.role === 'author';
  let inActiveReaders: typeof readers = [];
  let nonStarters: typeof readers = [];
  if (hasReaders) {
    inActiveReaders = readers.filter(
      (reader: any) =>
        reader.enabled &&
        reader.isInactive &&
        reader.invitation.status === 'ACCEPTED'
    );
    nonStarters = readers.filter(
      (reader: any) =>
        reader.enabled &&
        reader.isInactive &&
        !['ACCEPTED', 'DECLINED', 'ABANDONED'].includes(
          reader.invitation.status
        )
    );
  }
  const tabOptions = [
    {label: t('activeReaders'), count: totalActiveReaders,
      statuses: ['APPROVED', 'ACCEPTED', 'ABANDONED']},
    {label: t('PendingReadInvitations'), count: totalPendingInvitations,
      statuses: ['SENT', 'CREATED']},
    {label: t('PendingReadRequests'), count: totalPendingRequests,
      statuses: ['REQUESTED']},
  ];
  const canEdit = accessUtil.isAllowed(currentBook.access, ['edit']);

  return (
    <PaddedWrapper>
      <ReadingNotifications access={currentBook.access}
        inActiveReaders={inActiveReaders} nonStarters={nonStarters} />
      {userProfile && showProductLimitMessage && (
        <ProductLimitMessage reachedCap='maxReaders' basic={false} />
      )}
      <JoinBetaFormDesigner asModal trigger='Button'
        questions={currentBook.readerSignupSurvey?.questions || []}
        readOnly={!canEdit} />
      <div style={{display: 'flex', flexWrap: 'wrap', gap: 'var(--br-space-2)',
        alignItems: 'center', margin: 'var(--br-space-3) 0'}}>
        <Button onClick={() => setShowInvitationForm('single')}>
          {t('AddReader')}
        </Button>
        <nav aria-label={t('Readers')} style={{display: 'flex', flexWrap: 'wrap',
          gap: 'var(--br-space-1)', flex: 1}}>
          {tabOptions.map((tab, index) => (
            <Button key={tab.label} variant={menuActiveIndex === index ? 'secondary' : 'ghost'}
              aria-pressed={menuActiveIndex === index}
              onClick={() => setMenuActiveIndex(index)}>
              {tab.label} ({tab.count})
            </Button>
          ))}
        </nav>
        <details style={{position: 'relative'}}>
          <summary aria-label={t('MoreActions')} style={{cursor: 'pointer',
            padding: 'var(--br-space-2)'}}>⋮</summary>
          <div style={{position: 'absolute', right: 0, top: '100%', zIndex: 10,
            display: 'grid', gap: 'var(--br-space-1)', minWidth: 220,
            padding: 'var(--br-space-2)', background: 'var(--br-surface)',
            border: '1px solid var(--br-border)', borderRadius: 'var(--br-control-radius)'}}>
            <Button variant='ghost' onClick={downloadReaderList}>
              {t('DownloadReaderList')}
            </Button>
            <Button variant='ghost' onClick={() => setShowInvitationForm('single')}>
              {currentBook.privacy === 'unpublished'
                ? t('ChangePublishingStatusToAddReaders') : t('AddReader')}
            </Button>
            <Button variant='ghost' disabled={!canEdit || productCapUtil.isOnFreePlan({userProfile})}
              onClick={() => setShowInvitationForm('bulk')}>
              {t('BulkAddReaders')} (BETA)
            </Button>
          </div>
        </details>
      </div>
      <section aria-label={tabOptions[menuActiveIndex].label}>
        {renderReaderTable(tabOptions[menuActiveIndex].statuses)}
      </section>
      <Dialog open={showInvitationForm === 'single'} title={t('AddReader')}
        onClose={() => setShowInvitationForm(false)}
        actions={<Button variant='secondary' onClick={() => setShowInvitationForm(false)}>
          {t('Close')}
        </Button>}>
        <InvitationForm reader={undefined} disabled={false}
          invitationSent={() => setShowInvitationForm(false)} />
      </Dialog>
      <Dialog open={showInvitationForm === 'bulk'} title={t('BulkAddReaders')}
        onClose={() => setShowInvitationForm(false)}
        actions={<Button variant='secondary' onClick={() => setShowInvitationForm(false)}>
          {t('Close')}
        </Button>}>
        <BulkInvitationForm invitationSent={() => setShowInvitationForm(false)} />
      </Dialog>
      <Dialog open={Boolean(showReaderProfile)} title={t('Profile')}
        onClose={doCloseReaderProfile}
        actions={<Button variant='secondary' onClick={doCloseReaderProfile}>
          {t('Close')}
        </Button>}>
        {showReaderProfile && (
          <PublicUserProfile
            userId={typeof showReaderProfile === 'string'
              ? showReaderProfile : showReaderProfile.userId}
            autoOpenRating={typeof showReaderProfile === 'object'
              ? showReaderProfile.autoOpenRating : false} />
        )}
      </Dialog>
      {showBookRatingModal && (
        <BookSpecificRating open={Boolean(showBookRatingModal)}
          onClose={doCloseBookRatingModal} reader={showBookRatingModal}
          onRatingUpdated={onRatingUpdated} />
      )}
    </PaddedWrapper>
  );
};

export default ReaderList;
